import { computed, ref } from "vue";
import { useAuthStore } from "@/stores/auth";
import AuthService, { type DecodedJWTType } from "@/api/auth";
import type { ComputedRef } from "vue";
import { type Router } from "vue-router";
import { updateAbility } from "@/services/ability";
import { jwtDecode } from "jwt-decode";
import type { TUser } from "@/api/user";

type LoginManagerInstance = {
  logout: () => void;
  login: (email: string, password: string) => Promise<void>;
  isLogged: ComputedRef<boolean>;
  userInfo: ComputedRef<TUser | null>;
  accessToken: ComputedRef<string | null>;
  init: (router: Router) => Promise<void>;
};

export default (function () {
  const isLogged = computed(() => {
    const authStore = useAuthStore();
    return authStore.accessToken != null;
  });
  const userInfo = computed(() => {
    const authStore = useAuthStore();
    return authStore.userInfo;
  });
  const accessToken = computed(() => {
    const authStore = useAuthStore();
    return authStore.accessToken;
  });
  const privileges = ref<Array<string>>([]);

  let initialized = false;

  let refreshTokenInterval: number | null = null;

  const safetyExpirationTimeMargin = 120;

  function forgetMe() {
    const authStore = useAuthStore();
    authStore.setAuthTokens(null);
    authStore.setUserInfo(null);
  }

  async function logout() {
    stopRefreshTokenInterval();
    forgetMe();
  }

  async function loadMe() {
    const r = await AuthService.getMe();
    const authStore = useAuthStore();
    authStore.setUserInfo(<TUser>r.data);
    updateAbility(r.data);
  }

  async function login(email: string, password: string) {
    const r = await AuthService.login(email, password);
    const authStore = useAuthStore();
    authStore.setAuthTokens(r.access, r.refresh);
    await loadMe();
    startRefreshTokenInterval();
  }

  async function init(router: Router) {
    if (initialized) {
      return;
    }

    try {
      if (instance.isLogged.value) {
        await loadMe();
        startRefreshTokenInterval();
      }
    } catch (e: unknown) {
      console.error(e);
      forgetMe();
      router.push({
        name: "/login",
      });
    } finally {
      initialized = true;
    }
  }

  function startRefreshTokenInterval() {
    if (refreshTokenInterval) {
      return;
    }
    refreshTokenInterval = setInterval(() => refreshTokenIfNeeded(), 3000);
    refreshTokenIfNeeded();
  }

  function stopRefreshTokenInterval() {
    if (refreshTokenInterval) {
      clearInterval(refreshTokenInterval);
      refreshTokenInterval = null;
    }
  }

  async function refreshTokenIfNeeded() {
    const authStore = useAuthStore();

    if (authStore.accessToken) {
      let now = Date.now() / 1000;
      if (authStore.refreshToken && needRefresh(now) && canRefresh(now)) {
        await AuthService.refreshAccessToken(authStore.refreshToken).then(newTokens => {
          authStore.setAuthTokens(newTokens.access);
        });
      } else if (!canRefresh(now)) {
        logout();
      } else {
      }
    }
  }

  function needRefresh(now: number) {
    const authStore = useAuthStore();
    authStore.accessToken;

    if (!authStore.accessToken) {
      return false;
    }

    let jwt = jwtDecode(authStore.accessToken) as DecodedJWTType;
    if (now + safetyExpirationTimeMargin > jwt.exp) {
      return true;
    }
    return false;
  }

  function canRefresh(now: number) {
    const authStore = useAuthStore();

    if (!authStore.refreshToken) {
      return false;
    }
    let jwt = jwtDecode(authStore.refreshToken) as DecodedJWTType;
    if (now + safetyExpirationTimeMargin > jwt.exp) {
      // Refresh token is valid
      return false;
    }
    return true;
  }

  const instance: LoginManagerInstance = {
    logout,
    login,
    isLogged,
    userInfo,
    accessToken,
    init,
  };

  return () => {
    return instance;
  };
})();
